// Buy box for the 2026 product page (sections/main-product-pdp.liquid).
// Keeps the product form's quantity and selling_plan inputs in sync with the
// pack-size, purchase-type and frequency radios, and updates prices.
// product-form.js handles the actual add to cart.

if (!customElements.get('pdp-buy-box')) {
  customElements.define(
    'pdp-buy-box',
    class PdpBuyBox extends HTMLElement {
      connectedCallback() {
        this.config = JSON.parse(this.querySelector('[data-pdp-config]').textContent);
        this.form = document.getElementById(this.dataset.formId);
        this.quantityInput = this.form.querySelector('[data-quantity-input]');
        this.sellingPlanInput = this.form.querySelector('[data-selling-plan-input]');
        this.atcLabel = this.form.querySelector('[data-atc-label]');
        this.dialog = this.querySelector('dialog');
        this.sticky = this.querySelector('[data-sticky]');

        this.addEventListener('change', () => this.update());
        this.querySelectorAll('[data-open-info]').forEach((button) =>
          button.addEventListener('click', () => this.dialog?.showModal())
        );
        this.querySelectorAll('[data-close-info]').forEach((button) =>
          button.addEventListener('click', () => this.dialog?.close())
        );
        this.dialog?.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });

        this.setUpSticky();
        this.update();
      }

      get pack() {
        return this.querySelector('.pdp-pack__input:checked') || this.querySelector('.pdp-pack__input');
      }

      get plan() {
        return this.querySelector('.pdp-freq__input:checked') || this.querySelector('.pdp-freq__input');
      }

      get isSubscription() {
        return this.querySelector('.pdp-option__input:checked')?.value === 'subscription';
      }

      // Volume discount for a pack option (0.1 = 10% off), applied to one-time and subscription prices
      discountFor(input) {
        return Number(input.dataset.discount || 0) / 100;
      }

      // Line price in cents after the volume discount, matching Shopify's cart: the discount is
      // worked out per unit and rounded down to the cent, then multiplied by the quantity
      // (e.g. 10% off 2 × $27.99 = 2 × ($27.99 − $2.79) = $50.40, not $55.98 × 0.9 = $50.38)
      packTotal(unitPrice, input) {
        const unitDiscount = Math.floor(unitPrice * this.discountFor(input) + 1e-6);
        return (unitPrice - unitDiscount) * Number(input.value);
      }

      update() {
        const { variantPrice, unit, freeShippingCents, labels, available } = this.config;
        const plan = this.plan;
        const subUnitPrice = plan ? Number(plan.dataset.price) : variantPrice;
        const isSubscription = this.isSubscription && Boolean(plan);
        const unitPrice = isSubscription ? subUnitPrice : variantPrice;
        const pack = this.pack;
        const quantity = Number(pack.value);

        // Form inputs read by product-form.js
        this.quantityInput.value = quantity;
        this.sellingPlanInput.value = isSubscription ? plan.value : '';
        this.sellingPlanInput.disabled = !isSubscription;

        // Pack cards: price per bar/bag and free shipping tag at the current purchase type
        this.querySelectorAll('.pdp-pack__input').forEach((input) => {
          const card = input.closest('.pdp-pack');
          const total = this.packTotal(unitPrice, input);
          card.querySelector('[data-per-unit]').textContent = this.config.showPackTotal
            ? this.formatMoney(total)
            : `${this.formatMoney(total / Number(input.dataset.units))}/${unit}`;
          card.querySelector('[data-free-ship]').hidden = !(freeShippingCents > 0 && total >= freeShippingCents);
        });
        this.setText('[data-pack-summary]', pack.dataset.label);

        // Purchase options: crossed-out full price vs. price after subscription and volume discounts
        const fullPrice = variantPrice * quantity;
        const oneTimeTotal = this.packTotal(variantPrice, pack);
        const subTotal = this.packTotal(subUnitPrice, pack);
        this.setText('[data-sub-compare]', subTotal < fullPrice ? this.formatMoney(fullPrice) : '');
        this.setText('[data-sub-price]', this.formatMoney(subTotal));
        this.setText('[data-once-compare]', oneTimeTotal < fullPrice ? this.formatMoney(fullPrice) : '');
        this.setText('[data-once-price]', this.formatMoney(oneTimeTotal));
        this.setText(
          '[data-sub-save]',
          subTotal < fullPrice ? `You save ${this.formatMoney(fullPrice - subTotal)} every delivery` : ''
        );

        // Add to cart label, free shipping note, sticky bar
        const total = isSubscription ? subTotal : oneTimeTotal;
        if (available) {
          const label = `${isSubscription ? labels.subscribe : labels.addToCart} · ${this.formatMoney(total)}`;
          this.atcLabel.textContent = label;
          this.setText('[data-sticky-label]', label);
        }

        const shipNote = this.querySelector('[data-ship-note]');
        if (shipNote && freeShippingCents > 0) {
          const remaining = freeShippingCents - total;
          shipNote.textContent =
            remaining > 0
              ? `You’re ${this.formatMoney(remaining)} away from free shipping`
              : 'Free shipping included';
          shipNote.classList.toggle('pdp-ship--free', remaining <= 0);
        }
      }

      // Show the sticky bar on small screens once the main button has scrolled out of view above
      setUpSticky() {
        const cta = this.querySelector('[data-cta]');
        if (!this.sticky || !cta || !('IntersectionObserver' in window)) return;

        new IntersectionObserver(([entry]) => {
          this.sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
        }).observe(cta);
      }

      setText(selector, text) {
        const element = this.querySelector(selector);
        if (element && element.textContent !== text) element.textContent = text;
      }

      formatMoney(cents) {
        const currency = window.Shopify?.currency?.active || 'USD';
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(cents / 100);
      }
    }
  );
}

// Gallery thumbnails (desktop) and dots (phones) scroll the photo track; the active one follows swipes.
document.querySelectorAll('.pdp__gallery').forEach((gallery) => {
  const track = gallery.querySelector('.pdp__track');
  const thumbs = gallery.querySelectorAll('.pdp__thumb, .pdp__dot');
  if (!track || thumbs.length === 0) return;

  const setCurrent = (index) =>
    thumbs.forEach((thumb) => thumb.setAttribute('aria-current', Number(thumb.dataset.slide) === index ? 'true' : 'false'));

  thumbs.forEach((thumb) =>
    thumb.addEventListener('click', () => {
      const index = Number(thumb.dataset.slide);
      track.scrollTo({ left: track.clientWidth * index, behavior: 'smooth' });
      setCurrent(index);
    })
  );

  let frame;
  track.addEventListener('scroll', () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => setCurrent(Math.round(track.scrollLeft / track.clientWidth)));
  });
});
